import React from 'react';
import filter from 'lodash/filter';
import get from 'lodash/get';
import styled from 'styled-components';

import AdminTooltip from 'src/presentation/components/common/AdminTooltip';
import BusinessHoursEditor from './BusinessHoursEditor';
import { EmptyStateWrapper } from 'src/presentation/components/pages/shared/TableList';
import { isLoadingStatus } from 'src/presentation/hooks/useResource';
import PlacesUsedColumn from './components/PlacesUsedColumn';
import RouterLinkButton from 'src/presentation/components/RouterLinkButton';
import RowActions from './RowActions';
import { Row } from 'src/presentation/components/forms/Layout';
import TablePage, { TablePageRender } from 'src/presentation/components/pages/shared/TablePage';
import { CHAT_BRAND } from 'src/constants/branding';
import useFetchBusinessHours from './hooks/useFetchBusinessHours';
import useShowReferences from './hooks/useShowReferences';

const columnConfig = ({ openUseReferencesModal }) => [
  {
    attr: 'name',
    label: 'Name',
    customRenderer: item => renderName(item),
  },
  { attr: 'officeHours.timezone', label: 'Timezone', customColumnStyle: () => ({ width: '270px' }) },
  { attr: 'updatedAtString', label: 'Last Updated On', customColumnStyle: () => ({ width: '210px' }) },
  { attr: 'updatedByString', label: 'Last Updated By', customColumnStyle: () => ({ width: '140px' }) },
  {
    attr: 'officeHours.placesUsed',
    label: '# of Places Used',
    customRenderer: item => <PlacesUsedColumn item={item} openModal={openUseReferencesModal} />,
    customColumnStyle: () => ({ width: '145px' }),
  },
];

const renderName = item => {
  if (!item.primary) {
    return item.name;
  }

  return (
    <PrimaryNameContainer>
      <PrimaryName data-aid={`primary-business-hours-name-${item.id}`}>{item.name}</PrimaryName>
      <AdminTooltip delay={250} message="Primary Business Hours">
        <PrimaryPill data-aid={`primary-business-hours-pill-${item.id}`}>Primary</PrimaryPill>
      </AdminTooltip>
    </PrimaryNameContainer>
  );
};

const helpDocsRender = (
  <a target="_blank" rel="noopener noreferrer" href="https://help.gladly.com/docs/what-is-business-hours">
    Help Docs
  </a>
);

const BusinessHours: TablePageRender = ({ root }) => {
  const { renderModal: renderUseReferencesModal, openModal: openUseReferencesModal } = useShowReferences();
  const { businessHours, errors, loadingState, resetErrors } = useFetchBusinessHours();

  function filterFunc(items, searchString) {
    if (!searchString) {
      return items;
    }
    return filter(items, item => {
      const search = searchString.toLowerCase();
      return (
        get(item, 'name', '').toLowerCase().includes(search) ||
        get(item, 'officeHours.timezone', '').toLowerCase().includes(search)
      );
    });
  }

  const actions = (
    <RouterLinkButton data-aid="createBusinessHours" to={`${root}/new`}>
      Create Business Hours
    </RouterLinkButton>
  );

  const Description = (
    <div data-aid="business-hours-description-text">
      Set up business hours to be used with Rules. Once a Business Hours setting is used with Rules, it cannot be
      deleted. Note: Phone and {CHAT_BRAND.name} Business Hours must be configured directly in IVR or {CHAT_BRAND.name}{' '}
      settings. For more help, visit our {helpDocsRender}.
    </div>
  );

  const EmptyState = (
    <StyledEmptyStateWrapper data-aid="emptyMessage">
      <Row>
        <span>Need help setting up Business Hours? Check out our {helpDocsRender}</span>
      </Row>
      <Row>{actions}</Row>
    </StyledEmptyStateWrapper>
  );

  return (
    <>
      <TablePage
        actions={actions}
        columnConfig={columnConfig({ openUseReferencesModal })}
        data-aid="admin-business-hours-page"
        description={Description}
        editor={BusinessHoursEditor}
        emptyStateRender={EmptyState}
        errors={errors}
        filterFunc={filterFunc}
        isLoading={isLoadingStatus(loadingState)}
        items={businessHours}
        modalTopPadding={37}
        name="Business Hours"
        resetErrors={resetErrors}
        renderRowActions={props => <RowActions openReferencesModal={openUseReferencesModal} {...props} />}
        root={root}
      />
      {renderUseReferencesModal}
    </>
  );
};

const StyledEmptyStateWrapper = styled(EmptyStateWrapper)`
  height: 50vh;
`;

const PrimaryNameContainer = styled.div`
  display: flex;
`;

const PrimaryName = styled.span`
  margin-right: ${p => p.theme.spacing.medium};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PrimaryPill = styled.span`
  background-color: ${p => p.theme.colors.gray600};
  border-radius: ${p => p.theme.borderRadius.large};
  color: ${p => p.theme.colors.white};
  cursor: default;
  font-size: ${p => p.theme.fontSize.small};
  padding: ${p => p.theme.spacing.xsmall} ${p => p.theme.spacing.medium};
`;

export default BusinessHours;
